"use client";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { Search, ShoppingBag, ChevronDown, X, Phone, MapPinned } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useOutlet } from "@/context/OutletContext";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/fresh0meat_logo.png";
import { toast } from "sonner";
const LocationSelector = dynamic(() => import("./LocationSelector"), { ssr: false });
import { getCategories, getSubCategories } from "@/firebase/firestoreServices";
import type { Category, SubCategory } from "@/types";
import LazyImage from "@/components/LazyImage";

const Header = () => {
  const { profile } = useAuth();
  const { totalItems } = useCart();
  const { outlets, selectedOutlet, setSelectedOutlet, displayAddress } = useOutlet();
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Category mega menu state ─────────────────────────────────────────────
  const [showCatMenu, setShowCatMenu] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [catLoading, setCatLoading] = useState(false);
  const [activeCatId, setActiveCatId] = useState<string | null>(null);
  const [subCatMap, setSubCatMap] = useState<Record<string, SubCategory[]>>({});
  const [subCatLoading, setSubCatLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayLocation = displayAddress;

  // Sync search field with URL param
  useEffect(() => {
    if (searchParams) {
      setSearchQuery(searchParams.get("q") || "");
    }
  }, [searchParams]);

  // Close menu on route change
  useEffect(() => {
    setShowCatMenu(false);
  }, [pathname]);

  // Listen for custom event to open location selector from other pages (like Account)
  useEffect(() => {
    const handleOpen = () => setShowLocationSelector(true);
    window.addEventListener("openOutletPicker", handleOpen);
    return () => window.removeEventListener("openOutletPicker", handleOpen);
  }, []);

  // Close mega menu when clicking outside
  useEffect(() => {
    if (!showCatMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowCatMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCatMenu]);

  // Fetch categories when menu opens
  useEffect(() => {
    if (!showCatMenu || categories.length > 0) return;
    setCatLoading(true);
    getCategories()
      .then((data) => {
        setCategories(data as Category[]);
        if (data.length > 0) setActiveCatId((data[0] as Category).id);
      })
      .catch((e) => console.error("Error fetching categories:", e))
      .finally(() => setCatLoading(false));
  }, [showCatMenu, categories.length]);

  // Fetch subcategories when active category changes
  useEffect(() => {
    if (!activeCatId) return;
    if (subCatMap[activeCatId]) return; // already loaded
    setSubCatLoading(true);
    getSubCategories(activeCatId)
      .then((data) => setSubCatMap((prev) => ({ ...prev, [activeCatId]: data as SubCategory[] })))
      .catch((e) => console.error("Error fetching subCategories:", e))
      .finally(() => setSubCatLoading(false));
  }, [activeCatId, subCatMap]);

  // ── Search handler — navigate to home with ?q= param ────────────────────
  const handleSearch = (q: string) => {
    const trimmed = q.trim();
    if (trimmed) {
      router.push(`/?q=${encodeURIComponent(trimmed)}`);
    } else {
      router.push("/");
    }
  };

  // ── Debounced real-time search ────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const activeSubCats = activeCatId ? (subCatMap[activeCatId] ?? []) : [];

  return (
    <>
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
        {/* Top bar - delivery info */}
        <div className="hidden md:block bg-primary text-primary-foreground">
          <div className="container max-w-6xl mx-auto flex items-center justify-between px-4 py-1.5 text-xs">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowLocationSelector(true)}
                title="Select delivery location"
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              >
                <MapPinned className="w-3.5 h-3.5" />
                <span className="font-medium text-primary-foreground">{displayLocation}</span>
                <ChevronDown size={12} className="w-3 h-3" />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> Support: +91 95372-68055</span>
            </div>
          </div>
        </div>

        <div className="container max-w-6xl mx-auto">
          {/* Main header row */}
          <div className="flex items-center justify-between py-3 px-4 gap-6">
            <Link href="/" className="flex-shrink-0">
              <Image
                src={logo}
                alt="Fresh O Meat"
                width={160}
                height={48}
                priority
                fetchPriority="high"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav links */}
            <nav className="hidden md:flex items-center gap-1" ref={menuRef}>
              <Link
                href="/"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${pathname === "/"
                  ? "text-primary bg-primary/5"
                  : "text-foreground hover:text-primary hover:bg-muted"
                  }`}
              >
                Home
              </Link>

              {/* ── Categories button with mega menu ── */}
              <div className="relative">
                <button
                  onClick={() => setShowCatMenu((v) => !v)}
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${showCatMenu || pathname === "/categories"
                    ? "text-primary bg-primary/5"
                    : "text-foreground hover:text-primary hover:bg-muted"
                    }`}
                >
                  Categories
                  <motion.span animate={{ rotate: showCatMenu ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </motion.span>
                </button>

                <AnimatePresence>
                  {showCatMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.97 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="absolute left-0 top-full mt-2 w-[520px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 min-h-[320px]"
                    >
                      {catLoading ? (
                        <div className="flex items-center justify-center h-60">
                          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : (
                        <div className="flex min-h-[320px]">
                          {/* Left: category list */}
                          <div className="w-52 bg-gray-50 border-r border-gray-100 overflow-y-auto max-h-[440px]">
                            {categories.map((cat) => (
                              <button
                                key={cat.id}
                                onMouseEnter={() => setActiveCatId(cat.id)}
                                onClick={() => {
                                  router.push(`/category/${cat.id}`);
                                  setShowCatMenu(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors border-b border-gray-100 last:border-b-0 ${activeCatId === cat.id
                                  ? "bg-white font-semibold text-foreground"
                                  : "text-gray-600 hover:bg-white hover:text-foreground"
                                  }`}
                              >
                                {/* Category image */}
                                <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 border border-gray-200">
                                  {cat.image ? (
                                    <LazyImage
                                      src={cat.image}
                                      alt={cat.name}
                                      className="w-full h-full object-cover"
                                      wrapperClassName="w-full h-full"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-500">
                                      {cat.name[0]}
                                    </div>
                                  )}
                                </div>
                                <span className="text-sm leading-tight">{cat.name}</span>
                              </button>
                            ))}
                          </div>

                          {/* Right: subcategories */}
                          <div className="flex-1 overflow-y-auto p-2 max-h-[440px]">
                            {subCatLoading && !subCatMap[activeCatId ?? ""] ? (
                              <div className="flex items-center justify-center h-40">
                                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : activeSubCats.length === 0 ? (
                              <p className="text-sm text-gray-400 text-center mt-10">No subcategories</p>
                            ) : (
                              <div>
                                {activeSubCats.map((sub) => (
                                  <button
                                    key={sub.id}
                                    onClick={() => {
                                      router.push(`/category/${activeCatId}?sub=${sub.id}`);
                                      setShowCatMenu(false);
                                    }}
                                    className="w-full text-left px-4 py-3.5 text-sm text-gray-600 hover:text-primary hover:bg-primary/5 rounded-xl transition-colors border-b border-gray-50 last:border-b-0"
                                  >
                                    {sub.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                href="/favourites"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${pathname === "/favourites"
                  ? "text-primary bg-primary/5"
                  : "text-foreground hover:text-primary hover:bg-muted"
                  }`}
              >
                Favourites
              </Link>
              <Link
                href="/active-orders"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${pathname === "/active-orders"
                  ? "text-primary bg-primary/5"
                  : "text-foreground hover:text-primary hover:bg-muted"
                  }`}
              >
                Orders
              </Link>
              <Link
                href="/account"
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${pathname === "/account"
                  ? "text-primary bg-primary/5"
                  : "text-foreground hover:text-primary hover:bg-muted"
                  }`}
              >
                Account
              </Link>
            </nav>

            {/* Desktop search bar */}
            <div className="hidden md:block flex-1 max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" aria-hidden="true" />
                <input
                  type="text"
                  id="search-input"
                  name="search"
                  placeholder="Search chicken, mutton, fish..."
                  value={searchQuery}
                  aria-label="search"
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(searchQuery); }}
                  className="w-full pl-9 pr-7 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                {searchQuery && (
                  <button
                    title="Clear search"
                    onClick={() => { setSearchQuery(""); router.push("/"); }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Mobile search toggle */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                aria-label="Toggle search"
                title="Toggle search"
                className="md:hidden p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Search className="w-5 h-5 text-foreground" />
              </button>
              <Link href="/cart" aria-label={`Shopping cart with ${totalItems} items`} className="relative p-2 rounded-full hover:bg-muted transition-colors flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-foreground" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center" aria-hidden="true">
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Mobile location button */}
          <button
            onClick={() => setShowLocationSelector(true)}
            title="Select delivery location"
            className="md:hidden flex items-center gap-1.5 px-4 pb-2 text-sm"
          >
            <MapPinned className="w-4 h-4 text-primary" />
            <span className="font-medium text-foreground truncate max-w-[250px]">
              {displayLocation}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>

          {/* Mobile search bar */}
          <AnimatePresence>
            {showSearch && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden px-4 pb-3 md:hidden"
              >
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    id="mobile-search-input"
                    name="mobile-search"
                    placeholder="Search for chicken, mutton, fish..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch(searchQuery);
                        setShowSearch(false);
                      }
                    }}
                    className="w-full pl-10 pr-10 py-2.5 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      title="Clear search"
                      onClick={() => { handleSearchChange(""); setShowSearch(false); }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <LocationSelector
        open={showLocationSelector}
        onClose={() => setShowLocationSelector(false)}
      />
    </>
  );
};

export default Header;
